import type { CalendarEvent } from '../types.js';
import { isDateOnlyISO } from './dateUtils.js';

/**
 * Build a minimal RFC 5545 (`.ics`) file from a list of CalendarEvents.
 *
 * Scope:
 * - VEVENT per event with UID / DTSTAMP / DTSTART / DTEND / SUMMARY /
 *   DESCRIPTION (when `meta` present) / STATUS / RRULE (when present).
 * - All-day events use `VALUE=DATE` form.
 * - Timed events serialise to UTC (`Z` suffix). Sources that need wall-
 *   clock TZ-bound output should pre-format DTSTART with TZID — out of
 *   scope here, deliberately.
 * - CRLF line endings (RFC 5545 §3.1) and conservative line folding at
 *   75 octets so calendar clients (Apple, Google, Outlook) accept it.
 */
export function eventsToICS(
  events: readonly CalendarEvent[],
  options: { readonly calendarName?: string; readonly prodId?: string } = {},
): string {
  const { calendarName = 'Calendar', prodId = '-//Viquard//Calendar//EN' } = options;
  const dtstamp = formatUTC(new Date());
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${escapeText(prodId)}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(calendarName)}`,
  ];

  for (const ev of events) {
    if (ev.status === 'cancelled') continue; // Skip cancelled rows.
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${escapeText(ev.id)}@viquard.calendar`);
    lines.push(`DTSTAMP:${dtstamp}`);

    const allDay = ev.allDay === true || isDateOnlyISO(ev.startsAt);
    if (allDay) {
      const startKey = isDateOnlyISO(ev.startsAt) ? ev.startsAt : ev.startsAt.slice(0, 10);
      lines.push(`DTSTART;VALUE=DATE:${startKey.replace(/-/g, '')}`);
      const endKey = ev.endsAt !== null && ev.endsAt !== undefined
        ? (isDateOnlyISO(ev.endsAt) ? ev.endsAt : ev.endsAt.slice(0, 10))
        : nextDay(startKey);
      lines.push(`DTEND;VALUE=DATE:${endKey.replace(/-/g, '')}`);
    } else {
      const start = new Date(ev.startsAt);
      if (!Number.isNaN(start.getTime())) {
        lines.push(`DTSTART:${formatUTC(start)}`);
      }
      if (ev.endsAt !== null && ev.endsAt !== undefined) {
        const end = new Date(ev.endsAt);
        if (!Number.isNaN(end.getTime())) {
          lines.push(`DTEND:${formatUTC(end)}`);
        }
      }
    }

    lines.push(`SUMMARY:${escapeText(ev.title)}`);
    if (ev.meta !== undefined && ev.meta.length > 0) {
      lines.push(`DESCRIPTION:${escapeText(ev.meta)}`);
    }
    if (ev.recurrence !== null && ev.recurrence !== undefined && ev.recurrence.length > 0) {
      // Only serialise if it looks like a real RRULE body. The wire
      // contract caps `recurrence` length but accepts any string —
      // a hostile recurrence containing CR/LF or arbitrary control
      // chars would inject extra `:`-property lines into the
      // exported `.ics` (and therefore into whatever calendar app
      // imports it). Strip the leading `RRULE:` (caller passes the
      // body) and reject anything that isn't a flat sequence of
      // `KEY=VAL[,VAL];KEY=VAL…` characters. Anything off-grammar
      // is dropped silently — RRULE is best-effort metadata, the
      // event still exports without its repeat rule.
      const body = ev.recurrence.replace(/^RRULE:/i, '');
      if (isSafeRRuleBody(body)) {
        lines.push(`RRULE:${body}`);
      }
    }
    if (ev.status !== undefined) {
      const map: Record<NonNullable<CalendarEvent['status']>, string> = {
        scheduled: 'CONFIRMED',
        live: 'CONFIRMED',
        past: 'CONFIRMED',
        cancelled: 'CANCELLED',
      };
      lines.push(`STATUS:${map[ev.status]}`);
    }
    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.map(foldLine).join('\r\n') + '\r\n';
}

/** Trigger a download of the given ICS payload in the browser. No-op in
 *  non-DOM environments. */
export function downloadICS(filename: string, ics: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation so Safari finishes the download.
  setTimeout(() => { URL.revokeObjectURL(url); }, 1000);
}

function formatUTC(d: Date): string {
  const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

function escapeText(s: string): string {
  // RFC 5545 §3.3.11.
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\n|\r/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

/**
 * Conservative RRULE body whitelist. Only allows the alphanumeric/
 * key=value/list/separator characters that legitimate RRULE bodies use:
 *   - Letters + digits (FREQ, WEEKLY, COUNT, …, ISO date-time digits)
 *   - `=` between key and value
 *   - `;` between rule parts (`FREQ=DAILY;INTERVAL=2`)
 *   - `,` for value lists (`BYDAY=MO,WE,FR`)
 *   - `+ - :` for date-time values (`UNTIL=20251231T235959Z`,
 *     `BYHOUR=9,17`)
 *
 * Rejects CR/LF (line injection), spaces (out-of-grammar), and
 * any control character. Capped at 500 chars to mirror the wire
 * contract's max length.
 */
function isSafeRRuleBody(body: string): boolean {
  if (body.length === 0 || body.length > 500) return false;
  return /^[A-Za-z0-9=;,+\-:]+$/.test(body);
}

function nextDay(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/**
 * RFC 5545 §3.1: lines must not exceed 75 OCTETS (UTF-8 bytes), with
 * continuation lines beginning with a single space. We walk by byte to
 * place the cut, then rewind off any UTF-8 continuation byte (10xxxxxx)
 * so a multi-byte codepoint never gets split — important for non-ASCII
 * SUMMARY / DESCRIPTION values (Chinese, emoji, accented Latin).
 */
function foldLine(line: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(line);
  if (bytes.length <= 75) return line;
  const decoder = new TextDecoder();
  const chunks: string[] = [];
  let cursor = 0;
  // First chunk gets 75 bytes; subsequent continuation lines reserve
  // one byte for the leading space, so 74 bytes of payload each.
  let limit = 75;
  while (cursor < bytes.length) {
    let end = Math.min(cursor + limit, bytes.length);
    // Don't split mid-codepoint: rewind while landing on a UTF-8
    // continuation byte (high bits 10xxxxxx).
    while (end > cursor && end < bytes.length) {
      const byte = bytes[end];
      if (byte === undefined || (byte & 0xc0) !== 0x80) break;
      end--;
    }
    const piece = decoder.decode(bytes.subarray(cursor, end));
    chunks.push(chunks.length === 0 ? piece : ` ${piece}`);
    cursor = end;
    limit = 74;
  }
  return chunks.join('\r\n');
}
